import { ref, inject, type ComputedRef, type Ref } from 'vue'
import axios, { type AxiosProgressEvent, type AxiosInstance } from 'axios'

import { waitUntilFileViewable } from '@canvas-builder/util/file.util'
import type { PitcherInfo } from '@lib/types/app'
import type { FileCreateRequest, FileCreate, UploadCreateRequest, UploadCreate } from '@openapi'
import { AccessTypeEnum } from '@openapi'

export function useAssetUpload() {
  const isUploading = ref(false)
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>('pitcherInfo')
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
  const s3AxiosInstance = axios.create({ timeout: 0 })

  async function uploadAsset(file: File, onUploadProgress?: (progress: AxiosProgressEvent, file: File) => void) {
    if (!pitcherInfo?.value) throw new Error('Pitcher info is not provided')
    if (!restApiAxios?.value) throw new Error('HTTP client is not initialized')

    try {
      // Step 1: Get presigned URL from the server
      const uploadRequest: UploadCreateRequest = {
        name: file.name,
        size: file.size,
        instance_id: pitcherInfo.value.instanceId,
      }

      const uploadResponse = await restApiAxios.value.post<unknown, UploadCreate>('uploads/', uploadRequest)
      const { presigned_url, id: content_upload_id, type } = uploadResponse

      // Step 2: Upload the file directly to S3
      isUploading.value = true
      await s3AxiosInstance.put(presigned_url, file, {
        headers: {
          'Content-Type': type,
        },
        onUploadProgress: (e) => onUploadProgress?.(e, file),
      })

      // Step 3: Notify the server that the upload is complete
      const fileCreateRequest: FileCreateRequest = {
        instance_id: pitcherInfo.value.instanceId,
        content_upload_id,
        is_asset: true,
        access_type: pitcherInfo.value?.isAnyTypeOfAdmin ? AccessTypeEnum.PUBLIC : AccessTypeEnum.RESTRICTED,
      }

      const fileCreateResponse = await restApiAxios.value.post<unknown, FileCreate>('files/', fileCreateRequest)
      const fileData = await waitUntilFileViewable(fileCreateResponse.id, pitcherInfo.value)

      isUploading.value = false
      return fileData
    } catch (e) {
      isUploading.value = false
      throw e
    }
  }

  return {
    isUploading,
    uploadAsset,
  }
}
